import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { useTranslation } from 'react-i18next';
import { formatBillingCurrency } from '../../util';
import { formatPendingDate } from '../util';
import { ResolvedPlanDetails } from '../hooks/useSelectedPlanResolution';
import {
  BillingCadence,
  SummaryLine,
  computeCadenceAmounts,
  planDiscount,
} from './planSummaryShared';

type UpcomingLinesProps = {
  cadence: BillingCadence;
  corePlatformPlan: ResolvedPlanDetails;
  automationPlan: ResolvedPlanDetails;
  marketingPlan: ResolvedPlanDetails;
  numUsersForCalculation: number;
  proratedAmount?: number | null;
  firstBillingDate?: string;
  summaryTotal?: number | null;
  previewedCadence?: BillingCadence | null;
};

export const UpcomingLines = (props: UpcomingLinesProps) => {
  const {
    cadence,
    corePlatformPlan,
    automationPlan,
    marketingPlan,
    numUsersForCalculation,
    proratedAmount,
    firstBillingDate,
    summaryTotal,
    previewedCadence,
  } = props;

  const { t } = useTranslation();

  const {
    coreLine: coreLineAmount,
    automationLine: automationLineAmount,
    marketingLine: marketingLineAmount,
    total: computedTotal,
  } = computeCadenceAmounts(
    cadence,
    (corePlatformPlan?.price?.amount ?? 0) * numUsersForCalculation,
    planDiscount(corePlatformPlan),
    automationPlan?.price?.amount ?? 0,
    planDiscount(automationPlan),
    marketingPlan?.price?.amount ?? 0,
    planDiscount(marketingPlan)
  );

  const total =
    typeof summaryTotal === 'number' &&
    !Number.isNaN(summaryTotal) &&
    previewedCadence === cadence
      ? summaryTotal
      : computedTotal;

  const hasProration =
    typeof proratedAmount === 'number' && !Number.isNaN(proratedAmount);

  return (
    <div>
      <SummaryLine
        label={t('Kizen Native AI Platform - {{planName}}', {
          planName: corePlatformPlan?.name,
        })}
        detail={t('{{userCount}} Users', {
          userCount: numUsersForCalculation?.toLocaleString(),
        })}
        amount={coreLineAmount}
        symbol={corePlatformPlan?.price?.symbol}
      />
      <Spacer size={30} />
      <SummaryLine
        isAdditional
        label={t('Agentic Workflows - {{planName}}', {
          planName: automationPlan?.name,
        })}
        detail={t('{{limit}} Monthly Automated Tasks', {
          limit: automationPlan?.limits?.monthly_tasks_limit?.toLocaleString(),
        })}
        amount={automationLineAmount}
        symbol={automationPlan?.price?.symbol}
      />
      <Spacer size={30} />
      <SummaryLine
        isAdditional
        label={t('Marketing - {{planName}}', {
          planName: marketingPlan?.name,
        })}
        detail={t('{{limit}} Monthly Emails', {
          limit: marketingPlan?.limits?.monthly_emails_limit?.toLocaleString(),
        })}
        amount={marketingLineAmount}
        symbol={marketingPlan?.price?.symbol}
      />
      <Spacer size={30} />
      {hasProration ? (
        <>
          <div>
            <div className="flex items-center">
              <Typography size="lg" weight="semibold">
                {t('Prorated Amount Billed Today')}
              </Typography>
              <Typography className="ml-auto" size="lg" weight="semibold">
                {formatBillingCurrency(proratedAmount ?? 0)}
              </Typography>
            </div>
            {firstBillingDate ? (
              <>
                <Spacer size={10} />
                <Typography size="md">
                  {t('(through {{date}})', {
                    date: formatPendingDate(firstBillingDate),
                  })}
                </Typography>
              </>
            ) : null}
          </div>
          <Spacer size={20} />
        </>
      ) : null}
      <div className="flex items-center">
        <Typography size="lg" weight="semibold">
          {t('New {{period}} Total', {
            period: cadence === 'year' ? t('Annual') : t('Monthly'),
          })}
        </Typography>
        <Typography className="ml-auto" size="lg" weight="semibold">
          {formatBillingCurrency(total)}
        </Typography>
      </div>
    </div>
  );
};
